// The two anchoring looks of the foundation (R1125, `ui-map-interface.md`
// §6.1.5): card 19 BUOY COUNTDOWN, the AUTO look, and card 01 ANCHOR RING,
// the r19 ring that shipped before the field (R114).
//
// One entry per look id of `labels.toml anchor_styles` that this file owns.
// `RECIPES` holds the built cards and `PENDING` the ones still to build; the
// two lists are disjoint from those of every other recipe file, and
// `anchor-styles.test.ts` pins that.
//
// Every FIGURE is in the look's row of `data/presentation/anchor.toml`. A
// constant here is a PROPORTION of a figure the row carries: the quotient
// says what the card wrote, and it holds at any figure (R1120).

import {
  TAU, TWELVE, WATER_INK, arcOf, diamondFill, diamondOutline,
  disc, flashOf, line, ring, setRing, spanFigure,
  type AnchorCtx, type AnchorRecipe,
} from './anchor-recipe-kit';

// ---------------------------------------------------------------------------
// Card 01, ANCHOR RING
// ---------------------------------------------------------------------------


/**
 * The r19 ring of R114, figure for figure: a faint backing ring, and an arc
 * that fills clockwise from twelve o'clock with the share of cable out. A SET
 * cable closes the ring. An own hull and a hull of another faction each take
 * their own two alphas and their own arc width from the row, so the picture
 * is the one that shipped before the field.
 */
function anchorRing(c: AnchorCtx): void {
  const r = c.row;
  ring(c, c.x, c.y, r.rPx, r.width3Px, c.ink, c.own ? r.alpha : r.alpha2);
  if (c.p <= 0) return;
  const w = c.own ? r.widthPx : r.width2Px;
  const a = c.own ? r.alpha3 : r.alpha4;
  if (c.p >= 1) ring(c, c.x, c.y, r.rPx, w, c.ink, a);
  else arcOf(c, c.x, c.y, r.rPx, w, TWELVE, c.p * TAU, c.ink, a);
}

// ---------------------------------------------------------------------------
// Card 19, BUOY COUNTDOWN
// ---------------------------------------------------------------------------

/** The arrow head's half span, as a share of the foot's half width
 *  (the card's 3.8 against 4.5). */
const ARROW_HALF_K = 3.8 / 4.5;
/** The arrow head's stroke, as a share of the foot bar's (1.5 against 1.8). */
const ARROW_W_K = 1.5 / 1.8;
/** The floor point, as a share of the buoy's half diagonal (0.9 against 4). */
const FLOOR_R_K = 0.9 / 4;
/** Where the flash ring starts past the buoy, as a share of how far it grows
 *  (the card's 2 against 8). */
const FLASH_START_K = 2 / 8;
/** Where the numerals start right of the buoy, and how far under its middle
 *  they stand, as shares of their own size (the card's 5 and 0.5 at 9 px). */
const TEXT_DX_K = 5 / 9;
const TEXT_DY_K = 0.5 / 9;

/**
 * A chart buoy at a fixed offset beside the hull, a stalk under it that grows
 * with the share of cable out, and numerals for your own hull.
 *
 * SETTING: a hollow buoy, the stalk growing to an arrow head that points
 * down. SET: a solid buoy on a stalk that stands on a flat foot, with a ring
 * that flashes as it lands. WEIGHING: a hollow buoy, the stalk shortening,
 * and an arrow head above the buoy that points up.
 *
 * The numerals are your own hull's alone: the span of the cycle left while
 * the cable moves (`45s`, `2m 5s`), and the span of the beam order left on a
 * SET hull that has one (`3h 12m`), with their units (R2741). A fixture shows none, and a hull of another faction shows the buoy
 * alone. They keep their size on the screen at every zoom, and the buoy grows
 * when the map zooms out past `grow`.
 */
function buoyCountdown(c: AnchorCtx): void {
  setRing(c);
  const r = c.row, ink = c.ink, a = c.a;
  const up = c.phase === 'weighing', set = c.phase === 'set';
  const g = Math.max(1, r.grow / c.z);
  const bx = c.x + r.xPx, by = c.y + r.yPx;
  const d = r.rPx * g;
  const y0 = by + d;
  const y1 = y0 + (r.lengthPx + r.length2Px * c.p) * g;
  line(c, bx, y0, bx, y1, r.widthPx * g, ink, r.alpha * a);
  if (set) {
    const h = r.length3Px * g;
    line(c, bx - h, y1, bx + h, y1, r.width2Px * g, ink, r.alpha2 * a);
  } else {
    // The arrow head: down at the foot in SETTING, up above the buoy in
    // WEIGHING.
    const s = up ? -1 : 1;
    const ty = up ? by - d - r.length3Px * g : y1;
    const h = r.length3Px * ARROW_HALF_K * g, w = r.width2Px * ARROW_W_K * g;
    line(c, bx - h, ty - s * h, bx, ty, w, ink, r.alpha2 * a);
    line(c, bx, ty, bx + h, ty - s * h, w, ink, r.alpha2 * a);
    // The floor point: where the stalk ends when all the cable is out.
    disc(c, bx, y0 + (r.lengthPx + r.length2Px) * g, r.rPx * FLOOR_R_K * g,
      ink, r.alpha4 * a);
  }
  const fl = flashOf(c);
  if (fl > 0) {
    ring(c, bx, by, (r.rPx + r.r2Px * (FLASH_START_K + 1 - fl)) * g,
      r.width3Px * g, ink, r.alpha3 * fl * a);
  }
  // The buoy: solid when SET, a water core otherwise, so it reads hollow.
  if (set) diamondFill(c, bx, by, d, ink, a);
  else diamondFill(c, bx, by, d, WATER_INK, 1);
  diamondOutline(c, bx, by, d, r.width3Px * g, ink, r.alpha2 * a);
  if (!up) line(c, bx, by - d, bx, by - d - r.lengthPx * g, r.widthPx * g, ink, r.alpha * a);
  if (!c.own || r.textPx <= 0) return;
  const label = set
    ? (c.beam >= 0 ? spanFigure(c.leftSec) : '')
    : spanFigure(c.leftSec);
  if (label === '') return;
  c.text.run(label, bx + d, by, r.textPx * TEXT_DX_K, r.textPx * TEXT_DY_K,
    r.textPx, ink, r.alpha2);
}

/** The built looks of this file, by id. */
export const RECIPES: Readonly<Record<string, AnchorRecipe>> = {
  'buoy-countdown': buoyCountdown,
  'anchor-ring': anchorRing,
};

/** The looks of this file still to build. They draw the AUTO look. */
export const PENDING: readonly string[] = [];
